import React, { useEffect, useRef, useState } from 'react';
import { 
  FiEdit2, 
  FiPlus, 
  FiX, 
  FiCheck, 
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiHelpCircle,
  FiMessageSquare,
  FiUser,
  FiUsers,
  FiShield,
  FiAward,
  FiBookOpen,
  FiClock,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiTrash2,
  FiSave,
  FiXCircle
} from "react-icons/fi";
import { 
  MdOutlineHelp, 
  MdOutlineQuestionAnswer,
  MdOutlineVerified,
  MdOutlineCancel,
  MdOutlineCheckCircle,
  MdOutlinePending,
  MdOutlineEdit,
  MdOutlineDelete
} from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { getAppSettingList, updateAppSettings, updateAppSettingsStatus } from '../../Redux/appSettingSlice';
import FAQForm from './components/FAQForm';
import { toast } from "react-toastify";
import Loader from '../../components/Loader/Loader';

const FAQAdminSystem = () => {
    const dispatch = useDispatch();
    const { appSettingSlice: { loading, getAllAppSettings: { list = [] } = {} } = {} } = useSelector(state => state);
    const formRef = useRef(null);
    const [faqs, setFaqs] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
    const [formData, setFormData] = useState({
        id: '',
        question_name: '',
        question: '',
        answer: '',
        isActive: true
    });

    const apiFetchCred = {
        pageNo: '1',
        size: 50,
        keyWord: ''
    };

    // Category icons mapping
    const getCategoryIcon = (name) => {
        if (name?.includes('General')) return FiHelpCircle;
        if (name?.includes('Learners')) return FiUsers;
        if (name?.includes('Technical') || name?.includes('Security')) return FiShield;
        if (name?.includes('Community') || name?.includes('Support')) return FiMessageSquare;
        if (name?.includes('Educator')) return FiAward;
        return MdOutlineQuestionAnswer;
    };

    // Category colors
    const getCategoryColor = (name) => {
        if (name?.includes('General')) return 'from-blue-500 to-blue-600 bg-blue-50 text-blue-600';
        if (name?.includes('Learners')) return 'from-emerald-500 to-emerald-600 bg-emerald-50 text-emerald-600';
        if (name?.includes('Technical') || name?.includes('Security')) return 'from-purple-500 to-purple-600 bg-purple-50 text-purple-600';
        if (name?.includes('Community') || name?.includes('Support')) return 'from-amber-500 to-amber-600 bg-amber-50 text-amber-600';
        if (name?.includes('Educator')) return 'from-rose-500 to-rose-600 bg-rose-50 text-rose-600';
        return 'from-gray-500 to-gray-600 bg-gray-50 text-gray-600';
    };

    useEffect(() => {
        dispatch(getAppSettingList(apiFetchCred));
    }, [dispatch]);

    useEffect(() => {
        if (Array.isArray(list) && list.length > 0) {
            const newFaqs = list
                .filter(e =>
                    e?.name?.startsWith('General Questions') ||
                    e?.name?.startsWith('For Learners') ||
                    e?.name?.startsWith('Technical and Security Questions') ||
                    e?.name?.startsWith('Community and Support') ||
                    e?.name?.startsWith('For Educator')
                )
                .map(e => ({
                    id: e?._id,
                    question: e?.key,
                    question_name: e?.name,
                    answer: e?.value,
                    isActive: !e?.isDisable,
                    isDisable: e?.isDisable,
                    category: e?.name
                }));

            setFaqs(newFaqs);
        }
    }, [list]);

    const handleEdit = (faq) => {
        setEditingId(faq.id);
        setFormData({
            question: faq.question,
            question_name: faq.question_name,
            id: faq.id,
            answer: faq.answer,
            isActive: faq.isActive
        });
        // Scroll to form
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const payload = {
            id: formData.id,
            name: formData.question_name,
            key: formData.question,
            value: formData.answer
        };
        await dispatch(updateAppSettings({ Payload: payload, forReferesh: apiFetchCred }));
        handleCancel();
        toast.success("FAQ has been successfully updated.");
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({
            question: '',
            answer: '',
            question_name: '',
            id: '',
            isActive: true
        });
    };

    const handleOnChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const toggleActive = async (id) => {
        let payload = {
            "id": id
        };
        await dispatch(updateAppSettingsStatus({ Payload: payload, forReferesh: apiFetchCred }));
        toast.success("FAQ status has been successfully updated.");
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    // Filter FAQs based on search and status
    const filteredFaqs = faqs.filter(faq => {
        const matchesSearch = faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             faq.answer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             faq.question_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || 
                             (filterStatus === 'active' && faq.isActive) ||
                             (filterStatus === 'inactive' && !faq.isActive);
        return matchesSearch && matchesStatus;
    });

    // Calculate statistics
    const totalFaqs = faqs.length;
    const activeFaqs = faqs.filter(f => f.isActive).length;
    const inactiveFaqs = faqs.filter(f => !f.isActive).length;

    return (
        <div className="min-h-screen bg-gray-50/80" ref={formRef}>
            <Loader loading={loading} />

            {/* Header Banner */}
            <div className="bg-gradient-to-r from-[#855DF6] via-[#7B5CF6] to-[#715EFE] shadow-lg relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-12 -mb-12 animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
                
                <div className="relative z-10 px-4 md:px-7 py-6 md:py-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm shadow-lg">
                                <MdOutlineHelp className="w-5 h-5 text-white md:w-6 md:h-6" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white md:text-2xl dahboardFont tracking-tight">
                                    FAQ Question System
                                </h2>
                                <p className="text-sm text-white/70 hidden sm:block">
                                    Manage frequently asked questions and answers
                                </p>
                            </div>
                        </div>
                        {/* <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full backdrop-blur-sm">
                                <FiRefreshCw className="w-3.5 h-3.5 text-white/70" />
                                <span className="text-xs text-white/80">
                                    {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            </div>
                        </div> */}
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-3 mt-5 md:gap-4">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20 hover:bg-white/20 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-lg font-bold text-white uppercase md:text-lg">Total</p>
                                    <p className="text-xl font-bold text-white md:text-2xl mt-0.5">{totalFaqs}</p>
                                </div>
                                <div className="p-2 rounded-xl bg-blue-500/30 shadow-inner">
                                    <MdOutlineQuestionAnswer className="w-4 h-4 text-white md:w-5 md:h-5" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20 hover:bg-white/20 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-lg font-bold text-white uppercase md:text-lg">Active</p>
                                    <p className="text-xl font-bold text-emerald-300 md:text-2xl mt-0.5">{activeFaqs}</p>
                                </div>
                                <div className="p-2 rounded-xl bg-emerald-500/30 shadow-inner">
                                    <MdOutlineCheckCircle className="w-4 h-4 text-emerald-300 md:w-5 md:h-5" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20 hover:bg-white/20 transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-lg font-bold text-white uppercase md:text-lg">Inactive</p>
                                    <p className="text-xl font-bold text-rose-300 md:text-2xl mt-0.5">{inactiveFaqs}</p>
                                </div>
                                <div className="p-2 rounded-xl bg-rose-500/30 shadow-inner">
                                    <MdOutlineCancel className="w-4 h-4 text-rose-300 md:w-5 md:h-5" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="px-3 py-4 -mt-4 md:px-6 lg:px-10 md:py-6">
                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
                    {/* Form Section */}
                    {editingId && (
                        <div className="p-4 md:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
                                        <FiEdit2 className="w-4 h-4 text-[#715EFE]" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-700">
                                        Edit FAQ
                                    </h3>
                                </div>
                                <button
                                    onClick={handleCancel}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            </div>
                            <FAQForm
                                isEditing={!!editingId}
                                handleCancel={handleCancel}
                                formData={formData}
                                handleOnChange={handleOnChange}
                                handleUpdate={handleUpdate}
                            />
                        </div>
                    )}

                    {/* Search and Filter Bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-white">
                        <div className="flex items-center gap-3">
                            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
                                <FiHelpCircle className="w-4 h-4 text-[#715EFE]" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700">
                                    FAQ List
                                </h3>
                                <p className="text-xs text-gray-400 hidden sm:block">
                                    {filteredFaqs.length} of {totalFaqs} FAQs
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                            {/* Search */}
                            <div className="relative flex-1 sm:min-w-[200px]">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search FAQs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                                />
                            </div>
                            {/* Filter */}
                            <div className="relative">
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200 appearance-none bg-white"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* FAQ List */}
                    <div className="p-4 md:p-6">
                        <div className="space-y-3">
                            {filteredFaqs.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <div className="p-4 bg-gray-50 rounded-full mb-3">
                                        <MdOutlineHelp className="w-10 h-10 text-gray-300" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-500">No FAQs found</p>
                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                                </div>
                            ) : (
                                filteredFaqs.map((faq) => {
                                    const CategoryIcon = getCategoryIcon(faq.question_name);
                                    const categoryColor = getCategoryColor(faq.question_name);
                                    const isExpanded = expandedId === faq.id;

                                    return (
                                        <div 
                                            key={faq.id} 
                                            className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                                        >
                                            <div className="p-4 md:p-5">
                                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                            <div className={`p-1 rounded-lg ${categoryColor}`}>
                                                                <CategoryIcon className="w-3.5 h-3.5" />
                                                            </div>
                                                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                                                                {faq.question_name?.replace(/([A-Z])/g, ' $1').trim() || 'General'}
                                                            </span>
                                                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                                                faq.isActive
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                    : 'bg-rose-50 text-rose-700 border border-rose-200'
                                                            }`}>
                                                                {faq.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        
                                                        <h4 className="text-sm font-semibold text-gray-800 font-inter">
                                                            {faq.question}
                                                        </h4>
                                                        
                                                        <div className={`mt-2 ${!isExpanded ? 'line-clamp-2' : ''}`}>
                                                            <p className="text-sm text-gray-600 leading-relaxed font-inter">
                                                                {faq.answer}
                                                            </p>
                                                        </div>

                                                        {/* Expand/Collapse for long answers */}
                                                        {faq.answer?.length > 150 && (
                                                            <button
                                                                onClick={() => toggleExpand(faq.id)}
                                                                className="mt-1.5 text-xs font-medium text-[#715EFE] hover:text-[#855DF6] transition-colors duration-200 flex items-center gap-1"
                                                            >
                                                                {isExpanded ? (
                                                                    <>
                                                                        <FiChevronUp className="w-3.5 h-3.5" />
                                                                        Show less
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FiChevronDown className="w-3.5 h-3.5" />
                                                                        Read more
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        <button
                                                            onClick={() => toggleActive(faq.id)}
                                                            className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 flex items-center gap-1.5 ${
                                                                faq.isActive
                                                                    ? 'text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200'
                                                                    : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                                                            }`}
                                                            title={faq.isActive ? 'Deactivate FAQ' : 'Activate FAQ'}
                                                        >
                                                            {faq.isActive ? (
                                                                <FiXCircle className="w-3.5 h-3.5" />
                                                            ) : (
                                                                <FiCheck className="w-3.5 h-3.5" />
                                                            )}
                                                            {faq.isActive ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                        
                                                        <button
                                                            onClick={() => handleEdit(faq)}
                                                            className="p-2 text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                                                            title="Edit FAQ"
                                                        >
                                                            <FiEdit2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FAQAdminSystem;